<script setup>
import { computed } from 'vue';
import Spinner from 'shared/components/Spinner.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const props = defineProps({
  data: {
    type: Object,
    default: () => ({}),
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Destaques de Performance',
  },
});

const topByConversations = computed(() => props.data?.by_conversations || []);
const topByResolutions = computed(() => props.data?.by_resolutions || []);
const topByCsat = computed(() => props.data?.by_csat || []);
const topByResponseTime = computed(() => props.data?.by_response_time || []);

function formatTime(minutes) {
  if (!minutes || minutes === 0) return '0 min';
  if (minutes < 60) return `${minutes.toFixed(1)} min`;
  return `${(minutes / 60).toFixed(1)}h`;
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-trophy text-lg text-n-amber-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-64">
      <Spinner size="large" />
    </div>

    <div v-else class="grid grid-cols-2 gap-4">
      <!-- Top by Conversations -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2 mb-1">
          <span class="i-lucide-message-square text-sm text-n-blue-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Mais Conversas</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="(agent, index) in topByConversations.slice(0, 3)"
            :key="`conv-${agent.id}`"
            class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-1"
          >
            <span
              class="text-xs font-bold w-4"
              :class="index === 0 ? 'text-n-amber-11' : 'text-n-slate-10'"
            >
              {{ index + 1 }}
            </span>
            <Avatar :name="agent.name" :size="24" rounded-full />
            <span class="flex-1 text-xs text-n-slate-12 truncate">{{
              agent.name
            }}</span>
            <span class="text-sm font-bold text-n-blue-11">{{
              agent.value
            }}</span>
          </div>
        </div>
      </div>

      <!-- Top by Resolutions -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2 mb-1">
          <span class="i-lucide-check-circle text-sm text-n-teal-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Mais Resolucoes</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="(agent, index) in topByResolutions.slice(0, 3)"
            :key="`res-${agent.id}`"
            class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-1"
          >
            <span
              class="text-xs font-bold w-4"
              :class="index === 0 ? 'text-n-amber-11' : 'text-n-slate-10'"
            >
              {{ index + 1 }}
            </span>
            <Avatar :name="agent.name" :size="24" rounded-full />
            <span class="flex-1 text-xs text-n-slate-12 truncate">{{
              agent.name
            }}</span>
            <span class="text-sm font-bold text-n-teal-11">{{
              agent.value
            }}</span>
          </div>
        </div>
      </div>

      <!-- Top by CSAT -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2 mb-1">
          <span class="i-lucide-star text-sm text-n-amber-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Melhor CSAT</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="(agent, index) in topByCsat.slice(0, 3)"
            :key="`csat-${agent.id}`"
            class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-1"
          >
            <span
              class="text-xs font-bold w-4"
              :class="index === 0 ? 'text-n-amber-11' : 'text-n-slate-10'"
            >
              {{ index + 1 }}
            </span>
            <Avatar :name="agent.name" :size="24" rounded-full />
            <span class="flex-1 text-xs text-n-slate-12 truncate">{{
              agent.name
            }}</span>
            <span class="text-sm font-bold text-n-amber-11">{{ agent.value }}%</span>
          </div>
        </div>
      </div>

      <!-- Top by Response Time (fastest) -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2 mb-1">
          <span class="i-lucide-zap text-sm text-n-violet-10" />
          <span class="text-xs font-medium text-n-slate-11 uppercase">Mais Rapidos</span>
        </div>
        <div class="flex flex-col gap-2">
          <div
            v-for="(agent, index) in topByResponseTime.slice(0, 3)"
            :key="`time-${agent.id}`"
            class="flex items-center gap-2 p-2 rounded-lg bg-n-alpha-1"
          >
            <span
              class="text-xs font-bold w-4"
              :class="index === 0 ? 'text-n-amber-11' : 'text-n-slate-10'"
            >
              {{ index + 1 }}
            </span>
            <Avatar :name="agent.name" :size="24" rounded-full />
            <span class="flex-1 text-xs text-n-slate-12 truncate">{{
              agent.name
            }}</span>
            <span class="text-sm font-bold text-n-violet-11">{{
              formatTime(agent.value)
            }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
